import { DateTime, Duration } from 'luxon';

export const reviveDeparture = (dep: ApiDeparture): Departure => ({
  lineName: dep.short_name ?? '',
  direction: dep.headsign ?? '',
  time:
    typeof dep.next_departure === 'string'
      ? DateTime.fromISO(dep.next_departure)
      : DateTime.invalid('missing next_departure'),
  departsIn: Duration.fromMillis(0),
});

const isApiDeparture = (value: unknown): value is ApiDeparture =>
  typeof value === 'object' && value !== null;

/**
 * Turns the raw departures response into DepartureData for the given stop ids.
 * Every requested stop id gets an entry. A stop id whose value in the response
 * is missing or not an array gets an empty list. Elements that are not objects
 * and departures whose time does not parse are dropped so they never reach the
 * store.
 */
export const parseDepartures = (
  stopIds: string[],
  apiData: ApiDepartureData,
): DepartureData => {
  const data: DepartureData = {};
  stopIds.forEach((stopId) => {
    const deps = apiData[stopId];
    data[stopId] = Array.isArray(deps)
      ? deps
          .filter(isApiDeparture)
          .map(reviveDeparture)
          .filter((d) => d.time.isValid)
      : [];
  });
  return data;
};

export const removePassedDepartures = (
  departures: DepartureData,
  listMinimumTimeCutoff: number,
): DepartureData => {
  const now = DateTime.now().plus({ milliseconds: listMinimumTimeCutoff });
  const updated: DepartureData = {};
  Object.entries(departures).forEach(([stopId, deps]) => {
    for (let i = 0; i < deps.length; i++) {
      if (deps[i].time > now) {
        updated[stopId] = deps.slice(i);
        break;
      }
    }
  });
  return updated;
};

export const updateDepartsIn = (
  departures: DepartureData,
  listAmount: number,
): void => {
  Object.keys(departures).forEach((stopId) => {
    // Only need to update the first [listAmount] since we only show that many departures for each stop
    for (let j = 0; j < listAmount; j++) {
      if (departures[stopId][j]) {
        departures[stopId][j].departsIn = departures[stopId][j].time.diffNow();
      }
    }
  });
};

export interface ApiStop {
  stop_id: string;
  stop_name: string;
  stop_lat: number;
  stop_lon: number;
  location_type: null;
  geom: string;
}

export interface Stop {
  id: string;
  name: string;
  coordinate: { lng: number; lat: number };
}

export interface StopData {
  [stopId: string]: Stop;
}

/**
 * A departure row from gtfs-query. `short_name`, `headsign` and
 * `next_departure` are optional/nullable: GTFS rows without them exist and
 * used to crash the departures list (VIU-690).
 */
export interface ApiDeparture {
  arrival: string;
  date: string;
  departure: string;
  headsign?: string | null;
  id: string;
  lat: number;
  lon: number;
  long_name: string;
  name: string;
  next_arrival: string;
  next_departure?: string | null;
  short_name?: string | null;
}

export interface Departure {
  lineName: string;
  direction: string;
  time: DateTime;
  departsIn: Duration;
}

export interface DepartureWithStop extends Departure {
  stop: string;
}

export interface ApiDepartureData {
  [stopId: string]: ApiDeparture[];
}
export interface DepartureData {
  [stopId: string]: Departure[];
}
